import { useRef, useEffect, useState } from 'react';
import { User, Sparkles, Calendar, Map, MapPin, Camera } from 'lucide-react';
import { PhotoReveal, PhotoTriggerButton } from './PhotoReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contextual action markers that Chiara can embed in her responses
const CONTEXTUAL_TRIGGERS = {
  SHOW_CALENDAR: '[SHOW_CALENDAR]',
  SHOW_MAP: '[SHOW_MAP]',
  SHOW_AREA: '[SHOW_AREA:',  // [SHOW_AREA:Valle Umbra]
  SHOW_PHOTOS: '[SHOW_PHOTOS:',  // [SHOW_PHOTOS:provider:id:name]
};

export const ChatMessage = ({ message, isTyping = false, onContextualAction }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  // Photo state
  const [photoData, setPhotoData] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  
  // Extract contextual actions from message
  const extractContextualActions = (content) => {
    const actions = [];
    
    if (content?.includes(CONTEXTUAL_TRIGGERS.SHOW_CALENDAR)) {
      actions.push({ type: 'calendar', label: 'Apri Calendario' });
    }
    
    if (content?.includes(CONTEXTUAL_TRIGGERS.SHOW_MAP)) {
      actions.push({ type: 'map', label: 'Apri Mappa' });
    }
    
    // Extract area-specific map trigger
    const areaMatch = content?.match(/\[SHOW_AREA:([^\]]+)\]/);
    if (areaMatch) {
      actions.push({ type: 'map', label: `Vedi ${areaMatch[1]}`, area: areaMatch[1] });
    }
    
    // Extract photo trigger: [SHOW_PHOTOS:type:id:name]
    const photoMatch = content?.match(/\[SHOW_PHOTOS:([^:]+):([^:]+):([^\]]+)\]/);
    if (photoMatch) {
      actions.push({ 
        type: 'photos', 
        label: `Foto ${photoMatch[3]}`,
        entityType: photoMatch[1],
        entityId: photoMatch[2],
        entityName: photoMatch[3]
      });
    }
    
    return actions;
  };
  
  // Clean content by removing action markers
  const cleanContent = (content) => {
    if (!content) return '';
    return content
      .replace(/\[SHOW_CALENDAR\]/g, '')
      .replace(/\[SHOW_MAP\]/g, '')
      .replace(/\[SHOW_MAP_CHOICE\]/g, '')
      .replace(/\[SHOW_AREA:[^\]]+\]/g, '')
      .replace(/\[SHOW_PHOTOS:[^\]]+\]/g, '')
      .trim();
  };
  
  // Fetch photos when photo action is triggered
  const handleShowPhotos = async (action) => {
    if (photoLoading) return;
    setPhotoLoading(true);
    
    try {
      // First try the requested entity
      let response = await axios.post(`${API}/photos/validate`, {
        entity_type: action.entityType,
        entity_id: action.entityId,
        entity_name: action.entityName
      });
      
      // If provider has no photos, try area fallback automatically
      if (!response.data.valid && action.entityType === 'provider') {
        // Extract area from conversation context or use generic
        const areaFallbacks = ['valle_umbra', 'perugino', 'trasimeno'];
        for (const areaId of areaFallbacks) {
          response = await axios.get(`${API}/photos/area/${areaId}`);
          if (response.data.photos && response.data.photos.length > 0) {
            setPhotoData({
              photos: response.data.photos,
              entityName: response.data.entity_name || areaId.replace('_', ' '),
              entityType: 'area',
              fallbackUsed: true  // Mark as fallback
            });
            break;
          }
        }
      } else if (response.data.valid && response.data.photos.length > 0) {
        setPhotoData({
          photos: response.data.photos,
          entityName: response.data.entity_name,
          entityType: response.data.entity_type,
          fallbackUsed: response.data.fallback_used || false
        });
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setPhotoLoading(false);
    }
  };
  
  const contextualActions = !isUser && !isSystem ? extractContextualActions(message.content) : [];
  const displayContent = cleanContent(message.content);
  
  if (isSystem) {
    return (
      <div 
        className="text-xs text-gray-500 text-center py-2 animate-fade-in"
        data-testid="chat-message-system"
      >
        {message.content}
      </div>
    );
  }

  return (
    <div 
      className={`flex gap-3 animate-slide-up ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
      data-testid={`chat-message-${message.role}`}
    >
      {/* Avatar */}
      <div 
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser 
            ? 'bg-umbria-stone text-umbria-charcoal' 
            : 'bg-umbria-olive text-umbria-warm-white'
        }`}
      >
        {isUser ? <User size={16} /> : <Sparkles size={16} />}
      </div>
      
      {/* Message bubble */}
      <div className="flex flex-col gap-2 max-w-[85%]">
        <div className={isUser ? 'chat-bubble-user' : 'chat-bubble-assistant'}>
          {isTyping ? (
            <div className="flex gap-1 py-1">
              <span className="w-2 h-2 bg-umbria-olive/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-umbria-olive/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-umbria-olive/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-[15px] leading-relaxed">
              {formatMessage(displayContent)}
            </div>
          )}
        </div>
        
        {/* Contextual action buttons */}
        {contextualActions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1" data-testid="contextual-actions">
            {contextualActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (action.type === 'photos') {
                    handleShowPhotos(action);
                  } else {
                    onContextualAction?.(action);
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-umbria-olive/10 
                           hover:bg-umbria-olive/20 text-umbria-olive rounded-full transition-colors
                           border border-umbria-olive/20"
                data-testid={`action-${action.type}`}
              >
                {action.type === 'calendar' && <Calendar size={14} />}
                {action.type === 'map' && (action.area ? <MapPin size={14} /> : <Map size={14} />)}
                {action.type === 'photos' && <Camera size={14} />}
                {action.label}
              </button>
            ))}
          </div>
        )}
        
        {/* Inline photos when loaded */}
        {photoData && (
          <PhotoReveal
            photos={photoData.photos}
            entityName={photoData.entityName}
            entityType={photoData.entityType}
            fallbackUsed={photoData.fallbackUsed}
            isCompact={true}
          />
        )}
      </div>
    </div>
  );
};

// Format message content with basic markdown-like styling
const formatMessage = (content) => {
  if (!content) return null;
  
  // Split by lines and handle formatting
  const lines = content.split('\n');
  
  return lines.map((line, i) => {
    // Bold text: **text**
    let formattedLine = line.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
    
    // Italic text: *text*
    formattedLine = formattedLine.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Handle bullet points
    if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
      return (
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-umbria-olive">•</span>
          <span dangerouslySetInnerHTML={{ __html: formattedLine.replace(/^[-•]\s*/, '') }} />
        </div>
      );
    }
    
    // Handle numbered lists
    const numberedMatch = line.trim().match(/^(\d+)\.\s(.+)/);
    if (numberedMatch) {
      return (
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-umbria-olive font-medium">{numberedMatch[1]}.</span>
          <span dangerouslySetInnerHTML={{ __html: numberedMatch[2] }} />
        </div>
      );
    }
    
    // Handle "to be confirmed" markers
    if (line.includes('(da confermare:') || line.includes('(to be confirmed:')) {
      return (
        <div key={i} className="text-umbria-terracotta italic">
          {line}
        </div>
      );
    }
    
    return <div key={i} dangerouslySetInnerHTML={{ __html: formattedLine || '&nbsp;' }} />;
  });
};

export const ChatMessages = ({ messages, isLoading, onContextualAction }) => {
  const containerRef = useRef(null);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-6 space-y-4 scrollbar-thin"
      data-testid="chat-messages"
    >
      {messages.map((msg, index) => (
        <ChatMessage 
          key={msg.id || index} 
          message={msg} 
          onContextualAction={onContextualAction}
        />
      ))}
      
      {isLoading && (
        <ChatMessage 
          message={{ role: 'assistant', content: '' }} 
          isTyping={true} 
        />
      )}
    </div>
  );
};

export default ChatMessages;
